import React, { useState } from 'react';
import { Mail, ArrowLeft, Key } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import authService from '../services/authService';

interface PassRecoveryFormData {
  email: string;
}

const PassRecovery: React.FC = () => {
  const [formData, setFormData] = useState<PassRecoveryFormData>({
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (!formData.email) {
        throw new Error('Por favor, informe seu e-mail');
      }

      const response = await authService.recoverPassword({
        email: formData.email
      });

      if (response.success) {
        console.log('Password recovery email sent:', response.data);
        setIsSubmitted(true);
      } else {
        throw new Error(response.message || 'Erro ao enviar email de recuperação');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar e-mail de recuperação. Tente novamente.');
      console.error('Password recovery error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200 text-center">
            <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-gold-600" />
            </div>
            <h3 className="text-2xl font-display font-bold text-brown-800 mb-4">
              E-mail Enviado!
            </h3>
            <p className="text-brown-600 mb-6">
              Enviamos um link para redefinição de senha para <strong>{formData.email}</strong>.
              Verifique sua caixa de entrada e siga as instruções para criar uma nova senha.
            </p>
            <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 mb-6">
              <p className="text-gold-700 text-sm">
                <strong>Não recebeu o e-mail?</strong><br />
                Verifique sua pasta de spam ou lixo eletrônico. O link é válido por 24 horas.
              </p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => setIsSubmitted(false)}
                variant="secondary"
                className="w-full"
              >
                Reenviar E-mail
              </Button>
              <a href="/signin">
                <Button variant="primary" className="w-full">
                  Voltar ao Login
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-brown-800 mb-2">
            SantoGram
          </h1>
          <p className="text-brown-600">
            Recupere sua senha de acesso
          </p>
        </div>

        {/* Recovery Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200">
          {/* Back link */}
          <div className="mb-6">
            <a
              href="/signin"
              className="flex items-center gap-2 text-brown-600 hover:text-brown-800 text-sm font-medium"
            >
              <ArrowLeft size={16} />
              Voltar ao Login
            </a>
          </div>

          <div className="mb-6 text-center">
            <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-6 h-6 text-gold-600" />
            </div>
            <h2 className="text-2xl font-display font-bold text-brown-800 mb-2">
              Esqueceu sua Senha?
            </h2>
            <p className="text-brown-600 text-sm">
              Não se preocupe! Informe seu e-mail e enviaremos um link para você redefinir sua senha.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="relative">
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                label="E-mail da sua Conta"
                required
              />
              <Mail className="absolute right-3 top-[52px] transform -translate-y-1/2 w-5 h-5 text-brown-400 pointer-events-none" />
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-700 text-sm">
                <strong>Como funciona:</strong><br />
                1. Clique em "Enviar Link de Recuperação"<br />
                2. Verifique seu e-mail (pode estar no spam)<br />
                3. Clique no link recebido<br />
                4. Crie uma nova senha segura
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enviando...
                </>
              ) : (
                'Enviar Link de Recuperação'
              )}
            </Button>
          </form>

          {/* Alternative options */}
          <div className="mt-8 pt-6 border-t border-cream-200">
            <div className="text-center space-y-3">
              <p className="text-sm text-brown-600">
                Ainda com problemas para acessar?
              </p>
              <a
                href="/account-recovery"
                className="text-gold-600 hover:text-gold-700 font-medium text-sm"
              >
                Recuperar conta por telefone
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-brown-500">
            Lembre-se: Nunca compartilhe sua senha com outras pessoas.{' '}
            <span className="cursor-pointer text-gold-600 hover:text-gold-700">
              Saiba mais sobre segurança
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PassRecovery;